// src/components/PDF/CompressPDF.js
import React, { useState } from 'react';
import { Upload, Eye, Download, Minimize, Info, Settings, Zap } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import PDFPreviewSave from './PDFPreviewSave';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const CompressPDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [compressionLevel, setCompressionLevel] = useState(0.7);
  const [useAdvancedCompression, setUseAdvancedCompression] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState('');
  const [showPreviewSave, setShowPreviewSave] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        setCompressedFile(null);
        setCompressionProgress('');
      }
    }
  });

  const compressPDF = async () => {
    if (!pdfFile) {
      toast.error('Please select a PDF file to compress');
      return;
    }

    setIsLoading(true);
    setCompressionProgress('Starting compression...');
    
    try {
      let compressedBlob;
      
      if (useAdvancedCompression) {
        // Use advanced multi-pass compression
        compressedBlob = await pdfService.advancedCompressPDF(pdfFile, compressionLevel);
      } else {
        // Use standard compression
        compressedBlob = await pdfService.compressPDF(pdfFile, compressionLevel);
      }
      
      setCompressedFile({
        blob: compressedBlob,
        name: `compressed_${pdfFile.name}`,
        originalSize: pdfFile.size,
        compressedSize: compressedBlob.size
      });
      
      const reductionPercentage = ((pdfFile.size - compressedBlob.size) / pdfFile.size * 100);
      
      if (reductionPercentage > 0) {
        toast.success(`PDF compressed successfully! Size reduced by ${reductionPercentage.toFixed(1)}%`);
      } else {
        toast.success('PDF optimized! File structure has been improved for better performance.');
      }
      
      setCompressionProgress('');
      setShowPreviewSave(true);
    } catch (error) {
      toast.error('Error compressing PDF: ' + error.message);
      console.error(error);
      setCompressionProgress('');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCompressed = () => {
    requireAuth(() => {
      if (compressedFile) {
        const url = URL.createObjectURL(compressedFile.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = compressedFile.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetForm = () => {
    setPdfFile(null);
    setCompressedFile(null);
    setCompressionProgress('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCompressionLabel = (level) => {
    if (level <= 0.3) return 'Maximum';
    if (level <= 0.5) return 'High';
    if (level <= 0.7) return 'Medium';
    return 'Low';
  };

  const getCompressionDescription = (level) => {
    if (level <= 0.3) return 'Aggressive compression, may significantly reduce quality';
    if (level <= 0.5) return 'Strong compression with moderate quality impact';
    if (level <= 0.7) return 'Balanced compression preserving good quality';
    return 'Gentle compression maintaining high quality';
  };

  const handleSaveComplete = () => {
    toast.success('Compressed PDF saved to your document library!');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Compress PDF</h2>
      
      {/* File Upload Zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-[#232751] bg-[#232751]/5' 
            : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm font-medium text-gray-600 mb-2">
          {isDragActive ? 'Drop PDF here...' : 'Click to select or drag PDF here'}
        </p>
        <p className="text-xs text-gray-500">
          Upload a PDF to reduce its file size
        </p>
      </div>

      {/* Compression Settings */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Settings className="w-5 h-5 text-gray-600" />
          <h3 className="font-medium text-gray-700">Compression Settings</h3>
        </div>
        
        <div className="space-y-4">
          {/* Compression Level */}
          <div>
            <label htmlFor="compression" className="block text-sm font-medium text-gray-700 mb-2">
              Compression Level: {getCompressionLabel(compressionLevel)}
            </label>
            <input
              type="range"
              id="compression"
              min="0.1"
              max="1"
              step="0.1"
              value={compressionLevel}
              onChange={(e) => setCompressionLevel(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Maximum</span>
              <span>High</span>
              <span>Medium</span>
              <span>Low</span>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {getCompressionDescription(compressionLevel)}
            </p>
          </div>

          {/* Advanced Compression Toggle */}
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
            <div className="flex items-center space-x-3">
              <Zap className="w-5 h-5 text-orange-500" />
              <div>
                <label className="text-sm font-medium text-gray-700">Advanced Compression</label>
                <p className="text-xs text-gray-500">Multi-pass compression for better results (slower)</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useAdvancedCompression}
                onChange={(e) => setUseAdvancedCompression(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>

          {/* Compression Guide */}
          <div className="bg-[#232751]/5 border border-[#232751]/20 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-[#232751] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#232751]/80">
                <p className="font-medium mb-2">Compression Methods:</p>
                <ul className="space-y-1 text-xs">
                  <li>• <strong>Standard:</strong> PDF optimization and structure compression</li>
                  <li>• <strong>Advanced:</strong> Multi-pass compression with image optimization</li>
                  <li>• <strong>Best for images:</strong> PDFs with many photos or scanned pages</li>
                  <li>• <strong>Best for text:</strong> Documents with primarily text content</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected File */}
      {pdfFile && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Minimize className="w-6 h-6 text-orange-500" />
            <div>
              <p className="font-medium text-gray-700">{pdfFile.name}</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(pdfFile.size)} • {getCompressionLabel(compressionLevel)} compression
                {useAdvancedCompression && ' • Advanced mode'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      {compressionProgress && (
        <div className="bg-[#232751]/5 border border-[#232751]/20 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#232751]"></div>
            <p className="text-[#232751] font-medium">{compressionProgress}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={compressPDF}
          disabled={!pdfFile || isLoading}
          className="flex-1 bg-[#232751] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#1b1f43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Minimize className="w-5 h-5 mr-2" />
          {isLoading ? 'Compressing...' : 'Compress PDF'}
        </button>
        
        {pdfFile && (
          <button
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Compression Results */}
      {compressedFile && !showPreviewSave && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          {/* existing compression results JSX */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Minimize className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Compression Complete!</p>
                  <p className="text-sm text-green-600">Your compressed PDF is ready</p>
                </div>
              </div>
              <button
                onClick={() => setShowPreviewSave(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview & Save
              </button>
            </div>

            
            {/* Size Comparison */}
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Original Size</p>
                  <p className="font-medium text-gray-800">{formatFileSize(compressedFile.originalSize)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Compressed Size</p>
                  <p className="font-medium text-green-700">{formatFileSize(compressedFile.compressedSize)}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-green-100">
                {compressedFile.compressedSize < compressedFile.originalSize ? (
                  <p className="text-sm text-green-700">
                    <strong>Size Reduction:</strong>{' '}
                    {((compressedFile.originalSize - compressedFile.compressedSize) / compressedFile.originalSize * 100).toFixed(1)}%
                    {' '}({formatFileSize(compressedFile.originalSize - compressedFile.compressedSize)} saved)
                  </p>
                ) : (
                  <p className="text-sm text-green-700">
                    <strong>PDF Optimized:</strong> File structure improved for better performance
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {AuthModal}
      <PDFPreviewSave
        isOpen={showPreviewSave}
        onClose={() => setShowPreviewSave(false)}
        pdfBlob={compressedFile?.blob}
        fileName={compressedFile?.name}
        operationType="compressed"
        onSaveComplete={handleSaveComplete}
      />
    </div>
  );
};

export default CompressPDF;
